<template>
  <section id="contact" ref="targetElement" class="bg-gradient-to-br from-blue-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-20">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div 
        class="text-center mb-12 transition-all duration-700 ease-out"
        :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
      >
        <h2 class="text-4xl font-bold text-gray-900 dark:text-gray-100">Bereit für mehr Anfragen über Ihre Website?</h2>
        <div class="mt-4 h-0.5 w-16 bg-blue-600 dark:bg-blue-400 mx-auto"></div>
        <p class="mt-6 text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Im kostenlosen Erstgespräch schauen wir, was Ihre Website leisten soll und welcher nächste Schritt sinnvoll ist.
        </p>
      </div>
      
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>

      <div v-else-if="contactInfo" class="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
        <!-- Contact Info Card -->
        <div class="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700">
          <h3 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Kontaktinformationen</h3>
          <div class="space-y-4">
            <div class="flex items-start space-x-3">
              <UserIcon class="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <div class="text-sm text-gray-500 dark:text-gray-400">Name</div>
                <div class="text-gray-900 dark:text-gray-100 font-semibold">{{ contactInfo.name }}</div>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <BriefcaseIcon class="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <div class="text-sm text-gray-500 dark:text-gray-400">Tätigkeit</div>
                <div class="text-gray-900 dark:text-gray-100 font-semibold">{{ contactInfo.profession }}</div>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <MapPinIcon class="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <div class="text-sm text-gray-500 dark:text-gray-400">Standort</div>
                <div class="text-gray-900 dark:text-gray-100 font-semibold">{{ contactInfo.location }}</div>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <EnvelopeIcon class="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <div class="text-sm text-gray-500 dark:text-gray-400">E-Mail</div>
                <a :href="`mailto:${contactInfo.email}`" class="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 transition-colors font-semibold">
                  {{ contactInfo.email }}
                </a>
              </div>
            </div>
            <div v-if="contactInfo.phone" class="flex items-start space-x-3">
              <PhoneIcon class="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <div class="text-sm text-gray-500 dark:text-gray-400">Telefon</div>
                <a :href="`tel:${contactInfo.phone}`" class="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 transition-colors font-semibold">
                  {{ contactInfo.phone }}
                </a>
                <div v-if="contactInfo.phoneDisplay" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {{ contactInfo.phoneDisplay }}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- CTA Card -->
        <div class="bg-gradient-to-br from-blue-600 to-blue-500 dark:from-blue-500 dark:to-blue-600 p-8 rounded-xl shadow-lg shadow-blue-500/30 dark:shadow-blue-600/30 text-white">
          <h3 class="text-2xl font-bold mb-4">Kostenloses Erstgespräch</h3>
          <p class="mb-6 text-blue-50">
            Sie erzählen mir kurz von Ihrem Angebot und ich gebe Ihnen eine ehrliche Einschätzung, wie Ihre Website mehr Vertrauen und Anfragen erzeugen kann.
          </p>
          <ul class="space-y-3 mb-8">
            <li v-for="(benefit, index) in contactInfo.benefits" :key="index" class="flex items-start">
              <svg class="w-5 h-5 text-white mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span>{{ benefit }}</span>
            </li>
          </ul>
          <router-link 
            to="/contact"
            class="inline-flex items-center justify-center w-full px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors shadow-md hover:shadow-lg"
          >
            <EnvelopeIcon class="w-5 h-5 mr-2" />
            Kostenlose Beratung anfragen
          </router-link>
        </div>
      </div>
      
      <!-- Trust Badge -->
      <div class="text-center">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          <span class="inline-flex items-center">
            <svg class="w-4 h-4 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Sichere Kommunikation & Datenschutz garantiert
          </span>
        </p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { UserIcon, BriefcaseIcon, MapPinIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/vue/24/outline'
import { useScrollAnimation } from '../../composables/useScrollAnimation'
import { getContactInfo, getDefaultContactInfo, type ContactInfo } from '../../firebase/contact'
import { logger } from '../../utils/logger'

// Scroll-Animation mit Intersection Observer
const { isVisible, targetElement } = useScrollAnimation({
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
})

const contactInfo = ref<ContactInfo | null>(null)
const loading = ref(true)

onMounted(async () => {
  try {
    const data = await getContactInfo()
    if (data) {
      contactInfo.value = data
    } else {
      // Fallback zu Standard-Werten
      const defaultData = getDefaultContactInfo()
      contactInfo.value = {
        ...defaultData,
        updatedAt: new Date()
      } as ContactInfo
    }
  } catch (error) {
    logger.error('Fehler beim Laden der Kontaktdaten', error)
    // Fallback zu Standard-Werten
    const defaultData = getDefaultContactInfo()
    contactInfo.value = {
      ...defaultData,
      updatedAt: new Date()
    } as ContactInfo
  } finally {
    loading.value = false
  }
})
</script>



